import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import ReactDatePicker from "react-datepicker";
import {
  Box,
  Container,
  Grid,
  InputLabel,
  TextField,
  Tooltip,
  Typography,
  TextareaAutosize,
  FormControlLabel,
  Radio,
  FormControl,
  RadioGroup,
  Stack,
  Button,
  Divider,
  Snackbar,
  Alert,
  Autocomplete,
} from "@common-ui/ui";
import { Info } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updateAssetDetails } from "../../services";
import AlertDialog from "./AlertDialog";
import AssetDetails from "./AssetDetails";
import MandatoryIcon from "../../components/MandatoryIcon";

const INFO = {
  assetID: `The unique Asset Identifier as assigned for your Organization.`,
  make: `The Manufacturer, Make, or Brand that made the device. Usually as displayed on the product tag or front panel.`,
  model: `The Model Number, Catalog ID, or Product Code that identifies the product.`,
  description: `The Description, Nomenclature, or Name of the device. Usually as printed on the product tag or front panel.`,
  options: `Any options, add-ons, or upgrades installed or enabled for the product.`,
  oemSerialNumber: `The serial Number or ID assigned by the Manufacturer of the product. Select non-serialized if no serial number is present.
  Select illegible if present but unreadable.`,
  customSerialNumber: `A custom serial number assigned by the Owner or Client. Different from the Asset ID.`,
  // secondarySerialNumber: ``,
  secondaryAssetID: `The Asset Identifier as assigned by the Owner or Client. Different from the Asset ID.`,
  assetName: `A name to help identify the Asset. Defaults to Asset ID, Make, Model, Description. You can create any name you like, for example: High Capacity Pump #7`,
  comment: ``,
};

const EditAssetForm = ({
  asset,
  orgId,
  assetId,
  users,
  setIsLoading,
  appRole,
}) => {
  let router = useHistory();

  const [editMode, setEditMode] = useState(false);
  const [isMake, setIsMake] = useState("");
  const [isModel, setIsModel] = useState("");
  const [isSerialNumber, setIsSerialNumber] = useState("");
  const [toast, setToast] = useState([]);
  const [open, setOpen] = useState(false);
  const [isAssetAssigned, setIsAssetAssigned] = useState(false);

  let isCustomerOwned = asset?.customer;

  let doesUserHavePermission =
    appRole !== "TECHNICIAN" &&
    (appRole === "SUPERVISOR" || appRole === "ADMIN");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      assetID: "",
      make: "",
      model: "",
      description: "",
      options: "",
      oemSerialNumber: "",
      customSerialNumber: "",
      secondaryAssetID: "",
      assetName: "",
      comments: "",
      assignedAssetUser: null,
      assetIssuedOn: null,
      assetReturnedOn: null,
    },
    validationSchema: Yup.object({
      assetID: Yup.string().trim().required("Asset ID is required"),
      make: Yup.string().trim().required("Make is required"),
      model: Yup.string().trim().required("Model is required"),
      description: Yup.string().trim().required("Description is required"),
      options: Yup.string().trim(),
      oemSerialNumber: Yup.string()
        .trim()
        .required("OEM Serial Number is required"),
      customSerialNumber: Yup.string().trim(),
      secondaryAssetID: Yup.string().trim(),
      assetName: Yup.string()
        .trim()
        .required("Asset Name / Nick Name is required"),
      comments: Yup.string().trim(),
      assignedAssetUser: Yup.object().nullable(),
      assetIssuedOn: Yup.date().nullable().required("Issued On is required"),
      assetReturnedOn: Yup.date().nullable(),
    }),
    onSubmit: async () => {
      let {
        assetID,
        make,
        model,
        description,
        options,
        oemSerialNumber,
        customSerialNumber,
        secondaryAssetID,
        assetName,
        comments,
        assignedAssetUser,
        assetIssuedOn,
        assetReturnedOn,
      } = formik.values;

      const attributes = {
        id: assetID,
        secondary_id: secondaryAssetID,
        product_type: null,
        make: make,
        make_custom: true,
        model: model,
        model_custom: true,
        options: options,
        serial_number: oemSerialNumber,
        non_serialized: true,
        custom_serial_number: customSerialNumber,
        name: assetName,
        description: description,
        disposition: null,
        notes: comments,
        attributes: [
          // {
          //   name: "placeID",
          //   value: newPlaceParent ? newPlaceParent?.system_id : null,
          // },
        ],
        issued_to: assignedAssetUser?.username,
        issue_date: assetIssuedOn,
        return_date: assetReturnedOn,
      };

      try {
        setIsLoading(true);
        await onSubmit(attributes);
        setToast([
          ...toast,
          {
            message: "Success",
            open: true,
            severity: "success",
          },
        ]);
        handleClickOpen();
      } catch (error) {}
    },
  });

  const handleClickOpen = () => {
    setIsLoading(false);
    setOpen(true);
  };

  const onEditHandler = () => {
    setEditMode(true);
  };

  const handleClose = () => {
    router.push({
      pathname: `/asset-list`,
    });
    setOpen(false);
  };

  const onSubmit = async (attributes) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const payload = await updateAssetDetails(orgId, assetId, attributes);
        resolve({ payload: payload?.system_id });
      } catch (error) {
        reject(error);
      }
    });
  };

  useEffect(() => {
    if (asset && Object.keys(asset).length > 0) {
      formik.setFieldValue("assetID", asset.id);
      formik.setFieldValue("make", asset.make);
      setIsMake(asset.make.toString().toUpperCase());
      formik.setFieldValue("model", asset.model);
      setIsModel(asset.model.toString().toUpperCase());
      formik.setFieldValue("description", asset.description);
      formik.setFieldValue("options", asset.options ? asset.options : "");
      formik.setFieldValue(
        "oemSerialNumber",
        asset.serial_number ? asset.serial_number : ""
      );
      setIsSerialNumber(asset.serial_number.toString().toUpperCase());
      formik.setFieldValue(
        "customSerialNumber",
        asset.custom_serial_number ? asset.custom_serial_number : ""
      );
      formik.setFieldValue(
        "secondaryAssetID",
        asset.secondary_id ? asset.secondary_id : ""
      );
      formik.setFieldValue("assetName", asset.name);
      formik.setFieldValue("comments", asset.notes ? asset.notes : "");

      let assetUser = Array.from(users).filter(
        (user) => user.username === asset?.issued_to
      );
      formik.setFieldValue("assignedAssetUser", assetUser[0] || null);
      if (asset.issued_to) {
        setIsAssetAssigned(true);
      }
      formik.setFieldValue(
        "assetIssuedOn",
        asset.issue_date ? new Date(asset.issue_date) : null
      );
      formik.setFieldValue(
        "assetReturnedOn",
        asset.return_date ? new Date(asset.return_date) : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, users]);

  return (
    <>
      <Box
        component="main"
        sx={{
          alignItems: "center",
          display: "flex",
          flexGrow: 1,
          minHeight: "100%",
          backgroundColor: "#FFFFFF",
          ".MuiDataGrid-virtualScroller": {
            marginTop: "40px !important",
          },
          ".MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
          },
        }}
      >
        <Container component="main" sx={{ mt: 3 }}>
          <Box sx={{ my: 1, ml: 2, mb: 2 }}>
            <Typography color="textPrimary" variant="h4">
              Details
              {doesUserHavePermission ? (
                <Button
                  sx={{ ml: 2 }}
                  color="secondary"
                  size="small"
                  type="button"
                  variant="contained"
                  onClick={onEditHandler}
                >
                  Edit
                </Button>
              ) : (
                ""
              )}
            </Typography>
          </Box>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item md={8} xs={12} sx={{ px: 2, mb: 5 }}>
                {!editMode ? (
                  <AssetDetails asset={asset} />
                ) : (
                  <>
                    {/* Asset ID */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          Asset ID
                        </Typography>
                        <MandatoryIcon />
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        {" "}
                        <span>
                          <Tooltip placement="left" title={INFO.assetID}>
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          size="small"
                          error={Boolean(
                            formik.touched.assetID && formik.errors.assetID
                          )}
                          fullWidth
                          helperText={
                            formik.touched.assetID && formik.errors.assetID
                          }
                          placeholder="Type"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="assetID"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.assetID}
                        />
                      </div>
                    </div>

                    {/* Asset Make */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          Make
                        </Typography>
                        <MandatoryIcon />
                        <span>
                          {" "}
                          <FormControl size="small">
                            <RadioGroup
                              row
                              aria-labelledby="select-make-type"
                              value={isMake}
                              size="small"
                              onChange={(_event, value) => {
                                if (value === "UNKNOWN") {
                                  formik.setFieldValue("make", "Unknown");
                                } else {
                                  formik.setFieldValue("make", "");
                                }
                                setIsMake(value);
                              }}
                              name="make-type"
                            >
                              <FormControlLabel
                                value="KNOWN"
                                control={<Radio size="small" />}
                                label="Known"
                                size="small"
                              />
                              <FormControlLabel
                                value="UNKNOWN"
                                control={<Radio size="small" />}
                                label="Unknown"
                                size="small"
                              />
                              <FormControlLabel
                                value="CUSTOM"
                                control={<Radio size="small" />}
                                label="Custom"
                                size="small"
                              />
                            </RadioGroup>
                          </FormControl>
                        </span>
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        <span>
                          <Tooltip placement="left" title={INFO.make}>
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          disabled={isMake === "UNKNOWN" ? true : false}
                          size="small"
                          error={Boolean(
                            formik.touched.make && formik.errors.make
                          )}
                          fullWidth
                          helperText={formik.touched.make && formik.errors.make}
                          placeholder="Type"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="make"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.make}
                        />
                      </div>
                    </div>

                    {/* Asset Model */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          Model
                        </Typography>
                        <MandatoryIcon />
                        <span>
                          {" "}
                          <FormControl size="small">
                            <RadioGroup
                              row
                              aria-labelledby="select-model-type"
                              value={isModel}
                              size="small"
                              onChange={(_event, value) => {
                                if (value === "UNKNOWN") {
                                  formik.setFieldValue("model", "Unknown");
                                } else {
                                  formik.setFieldValue("model", "");
                                }
                                setIsModel(value);
                              }}
                              name="model-type"
                            >
                              <FormControlLabel
                                value="KNOWN"
                                control={<Radio size="small" />}
                                label="Known"
                                size="small"
                              />
                              <FormControlLabel
                                value="UNKNOWN"
                                control={<Radio size="small" />}
                                label="Unknown"
                                size="small"
                              />
                              <FormControlLabel
                                value="CUSTOM"
                                control={<Radio size="small" />}
                                label="Custom"
                                size="small"
                              />
                            </RadioGroup>
                          </FormControl>
                        </span>
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        <span>
                          <Tooltip placement="left" title={INFO.model}>
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          disabled={isModel === "UNKNOWN" ? true : false}
                          size="small"
                          error={Boolean(
                            formik.touched.model && formik.errors.model
                          )}
                          fullWidth
                          helperText={
                            formik.touched.model && formik.errors.model
                          }
                          placeholder="Type"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="model"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.model}
                        />
                      </div>
                    </div>

                    {/* Asset Description */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          Description
                        </Typography>
                        <MandatoryIcon />
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        {" "}
                        <span>
                          <Tooltip placement="left" title={INFO.description}>
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          size="small"
                          error={Boolean(
                            formik.touched.description &&
                              formik.errors.description
                          )}
                          fullWidth
                          helperText={
                            formik.touched.description &&
                            formik.errors.description
                          }
                          placeholder="Type"
                          // label="Company or Organization Name"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="description"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.description}
                        />
                      </div>
                    </div>

                    {/* Asset Original Equipment Manufacturer (OEM) Number */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          OEM Serial Number
                        </Typography>
                        <MandatoryIcon />
                        <span>
                          {" "}
                          <FormControl size="small">
                            <RadioGroup
                              row
                              aria-labelledby="select-oemSerialNumber-type"
                              value={isSerialNumber}
                              size="small"
                              onChange={(_event, value) => {
                                if (value === "NON_SERIALIZED") {
                                  formik.setFieldValue(
                                    "oemSerialNumber",
                                    "Non-Serialized"
                                  );
                                } else if (value === "ILLEGIBLE") {
                                  formik.setFieldValue(
                                    "oemSerialNumber",
                                    "Illegible"
                                  );
                                } else {
                                  formik.setFieldValue("oemSerialNumber", "");
                                }
                                setIsSerialNumber(value);
                              }}
                              name="oemSerialNumber-type"
                            >
                              <FormControlLabel
                                value="SERIALIZED"
                                control={<Radio size="small" />}
                                label="Serialized"
                                size="small"
                              />
                              <FormControlLabel
                                value="NON_SERIALIZED"
                                control={<Radio size="small" />}
                                label="Non-Serialized"
                                size="small"
                              />
                              <FormControlLabel
                                value="ILLEGIBLE"
                                control={<Radio size="small" />}
                                label="Illegible"
                                size="small"
                              />
                            </RadioGroup>
                          </FormControl>
                        </span>
                      </InputLabel>

                      <div style={{ display: "flex" }}>
                        {" "}
                        <span>
                          <Tooltip
                            placement="left"
                            title={INFO.oemSerialNumber}
                          >
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          disabled={
                            isSerialNumber !== "SERIALIZED" ? true : false
                          }
                          size="small"
                          error={Boolean(
                            formik.touched.oemSerialNumber &&
                              formik.errors.oemSerialNumber
                          )}
                          fullWidth
                          helperText={
                            formik.touched.oemSerialNumber &&
                            formik.errors.oemSerialNumber
                          }
                          placeholder="Type"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="oemSerialNumber"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.oemSerialNumber}
                        />
                      </div>
                    </div>

                    {/* Asset Custom Serial Number */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          Custom Serial Number
                        </Typography>
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        {" "}
                        <span>
                          <Tooltip
                            placement="left"
                            title={INFO.customSerialNumber}
                          >
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          size="small"
                          error={Boolean(
                            formik.touched.customSerialNumber &&
                              formik.errors.customSerialNumber
                          )}
                          fullWidth
                          helperText={
                            formik.touched.customSerialNumber &&
                            formik.errors.customSerialNumber
                          }
                          placeholder="Type"
                          // label="Company or Organization Name"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="customSerialNumber"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.customSerialNumber}
                        />
                      </div>
                    </div>

                    {/* Asset Options */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          Options
                        </Typography>
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        <span>
                          <Tooltip placement="left" title={INFO.options}>
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          size="small"
                          error={Boolean(
                            formik.touched.options && formik.errors.options
                          )}
                          fullWidth
                          helperText={
                            formik.touched.options && formik.errors.options
                          }
                          placeholder="Type"
                          // label="Company or Organization Name"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="options"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.options}
                        />
                      </div>
                    </div>

                    {/* Asset Secondary Asset ID */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          Secondary Asset ID
                        </Typography>
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        <span>
                          <Tooltip
                            placement="left"
                            title={INFO.secondaryAssetID}
                          >
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          size="small"
                          error={Boolean(
                            formik.touched.secondaryAssetID &&
                              formik.errors.secondaryAssetID
                          )}
                          fullWidth
                          helperText={
                            formik.touched.secondaryAssetID &&
                            formik.errors.secondaryAssetID
                          }
                          placeholder="Type"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="secondaryAssetID"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.secondaryAssetID}
                        />
                      </div>
                    </div>

                    {/* Asset Name / Nick Name */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          Asset Name / Nick Name
                        </Typography>
                        <MandatoryIcon />
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        <span>
                          <Tooltip placement="left" title={INFO.assetName}>
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          size="small"
                          error={Boolean(
                            formik.touched.assetName && formik.errors.assetName
                          )}
                          fullWidth
                          helperText={
                            formik.touched.assetName && formik.errors.assetName
                          }
                          placeholder="Type"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="assetName"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.assetName}
                        />
                      </div>
                    </div>

                    {/* Comments on the Asset */}
                    <div>
                      <InputLabel
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography sx={{ m: 0, ml: 2.5 }} fontWeight="700">
                          Comments
                        </Typography>
                      </InputLabel>
                      <div style={{ display: "flex" }}>
                        <span
                          style={{
                            visibility: "hidden",
                            opacity: 0,
                            cursor: "none",
                          }}
                        >
                          <Tooltip placement="left" title={INFO.assetID}>
                            <Info sx={{ fontSize: 14, pr: 0.5 }} color="info" />
                          </Tooltip>
                        </span>
                        <TextField
                          autoComplete="off"
                          size="small"
                          error={Boolean(
                            formik.touched.comments && formik.errors.comments
                          )}
                          fullWidth
                          helperText={
                            formik.touched.comments && formik.errors.comments
                          }
                          placeholder="Type"
                          margin="normal"
                          sx={{ mt: 0 }}
                          name="comments"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.comments}
                          InputProps={{
                            inputComponent: TextareaAutosize,
                            inputProps: { minRows: 4 },
                          }}
                          variant="outlined"
                        />
                      </div>
                    </div>
                  </>
                )}
              </Grid>

              <Grid item md={4} xs={12} sx={{ px: 2 }}>
                {/* Asset Assignment */}
                <div>
                  <InputLabel>
                    <Typography fontWeight="700">Issued To</Typography>
                  </InputLabel>
                  <Autocomplete
                    options={users}
                    size="small"
                    disabled={isCustomerOwned ? true : false}
                    value={formik.values.assignedAssetUser}
                    fullWidth
                    onChange={(_e, value) => {
                      formik.setFieldValue("assignedAssetUser", value);
                      if (value && value.username !== null) {
                        setIsAssetAssigned(true);
                      } else {
                        setIsAssetAssigned(false);
                        formik.setFieldValue("assetIssuedOn", "");
                      }
                    }}
                    getOptionLabel={(option) => option.username}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select User"
                        inputProps={{
                          ...params.inputProps,
                        }}
                        name="assignedAssetUser"
                        id="assignedAssetUser"
                        error={Boolean(
                          formik.touched.assignedAssetUser &&
                            formik.errors.assignedAssetUser
                        )}
                        helperText={
                          formik.touched.assignedAssetUser &&
                          formik.errors.assignedAssetUser
                        }
                        onBlur={formik.handleBlur}
                      />
                    )}
                  />
                </div>

                {/* Asset Assigned Date */}
                <div>
                  <InputLabel>
                    <Typography sx={{ mt: 1 }} fontWeight="700">
                      Issued On
                      {isAssetAssigned && <MandatoryIcon />}
                    </Typography>
                  </InputLabel>
                  <ReactDatePicker
                    disabled={!isAssetAssigned}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    todayButton="Today"
                    dropdownMode="select"
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Issued On"
                    selected={formik.values.assetIssuedOn}
                    minDate={formik.values.assetIssuedOn ?? null}
                    onChange={(date) => {
                      formik.setFieldValue("assetIssuedOn", date);
                    }}
                    customInput={
                      <TextField
                        fullWidth
                        size="small"
                        name="assetIssuedOn"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.assetIssuedOn}
                        error={Boolean(
                          formik.touched.assetIssuedOn &&
                            formik.errors.assetIssuedOn
                        )}
                        helperText={
                          formik.touched.assetIssuedOn &&
                          formik.errors.assetIssuedOn
                        }
                      />
                    }
                  />
                </div>

                {/* Asset Return Date */}
                <div>
                  <InputLabel>
                    <Typography sx={{ mt: 1 }} fontWeight="700">
                      Return On
                    </Typography>
                  </InputLabel>
                  <ReactDatePicker
                    disabled={!isAssetAssigned}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    todayButton="Today"
                    dropdownMode="select"
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Return On"
                    selected={formik.values.assetReturnedOn}
                    minDate={formik.values.assetReturnedOn ?? null}
                    onChange={(date) => {
                      formik.setFieldValue("assetReturnedOn", date);
                    }}
                    customInput={
                      <TextField
                        fullWidth
                        size="small"
                        name="assetReturnedOn"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.assetReturnedOn}
                        error={Boolean(
                          formik.touched.assetReturnedOn &&
                            formik.errors.assetReturnedOn
                        )}
                        helperText={
                          formik.touched.assetReturnedOn &&
                          formik.errors.assetReturnedOn
                        }
                      />
                    }
                  />
                </div>
              </Grid>
            </Grid>

            <Stack>
              <Divider sx={{ py: 1 }} />
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="flex-end"
                spacing={{ xs: 1, md: 2 }}
                sx={{ py: 2 }}
              >
                <div>
                  <Button
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={() => router.goBack()}
                    variant="text"
                  >
                    Cancel
                  </Button>
                </div>
                <div>
                  <Button
                    color="secondary"
                    disabled={!(formik.isValid && formik.dirty)}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    Save
                  </Button>
                </div>
              </Stack>
            </Stack>
          </form>
          {toast.map((item, id) => {
            return (
              <Snackbar
                key={id}
                open={item.open}
                autoHideDuration={6000}
                onClose={() => {
                  setToast(toast.filter((v, i) => i !== id));
                }}
              >
                <Alert
                  onClose={() => {
                    setToast(toast.filter((v, i) => i !== id));
                  }}
                  severity={item.severity}
                  sx={{ width: "100%" }}
                  variant="filled"
                >
                  {item.message}
                </Alert>
              </Snackbar>
            );
          })}
        </Container>
      </Box>
      <AlertDialog edit="true" handleClose={handleClose} open={open} />
    </>
  );
};

export default EditAssetForm;
